import { useMutation, useQuery, useQueryClient, UseMutationOptions, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';

import { formatErrorMessage } from '@/utils/format-error-message';
import { AllocationResult, isAllocationValidationError } from '@oneview/api';
import { useDispatch } from '@oneview/redux';
import { SuccessClientResponse } from '@orbis-systems/orbis-api-clients';
import type { Order } from '@orbis-systems/orbis-api-middleware';
import { showToast } from '@/redux';
import { AxiosError, isAxiosError } from 'axios';
import {
    tradingMutations,
    tradingQueries,
    type TradingCancelOrderParams,
    type TradingGetOrderDetailsParams,
    type TradingModifyOrderParams,
} from './queries';
import { branchQueries } from '../branch';
import { STALE_TIME_10_MIN } from '../constants';
import { ordersQueries } from '../orders';
import { userQueries } from '../user';
import { get, set } from 'lodash';
import { orbis } from '../api';

export type TriggerAllocationMutationParams = UseMutationOptions<
    SuccessClientResponse<AllocationResult>,
    AxiosError,
    string
>;

export interface TriggerAllocationResult {
    data: AllocationResult[];
    errors: Record<string, string[]>;
}

export interface UseGetOrderDetailsQueryParams extends TradingGetOrderDetailsParams {
    enabled?: boolean;
    staleTime?: number;
}

const onError = (error: AxiosError) => toast.error(formatErrorMessage(error.response?.data));

function selectTradingOrderStatusOrder(
    response: Awaited<ReturnType<typeof orbis.orders.status>> | undefined,
): Order | null {
    return response?.data ?? null;
}

export const useGetTradingOrderStatusQuery = (params: UseGetOrderDetailsQueryParams) => {
    const { enabled, staleTime = STALE_TIME_10_MIN, ...queryParams } = params;

    return useQuery({
        ...tradingQueries.orderStatus(queryParams),
        enabled,
        staleTime,
        select: selectTradingOrderStatusOrder,
    });
};

export const useTriggerAllocationMutation = (options?: TriggerAllocationMutationParams) => {
    const dispatch = useDispatch();

    const mutation = useMutation({
        mutationKey: ['triggerAllocation'],
        mutationFn: (allocationRef: string) => orbis.advisory.allocations.trigger({ allocationRef }),
        ...options,
    });

    const triggerAllocation = async (allocationRefs: string[]) => {
        const data: TriggerAllocationResult['data'] = [];
        const errors: TriggerAllocationResult['errors'] = {};

        for (const ref of allocationRefs) {
            try {
                const result = await mutation.mutateAsync(ref);
                data.push(result.data);
            } catch (error) {
                if (isAxiosError(error)) {
                    const errorData = error.response?.data;
                    if (isAllocationValidationError(errorData)) {
                        const refError = (error.response?.data.errors || []) as string[];

                        for (let i = 0; i < refError.length; i += 2) {
                            const errorMessage = refError[i + 1]!.split('=')[1];
                            set(errors, ref, [...(get(errors, ref, []) as string[]), errorMessage]);
                        }
                    } else {
                        set(errors, ref, [...(get(errors, ref, []) as string[]), 'Allocation trigger failed']);
                    }
                    dispatch(showToast(`Allocation trigger ${ref} failed`));
                }
            }
        }
        dispatch(showToast('Allocation  has been triggered'));

        return { data, errors };
    };

    return {
        triggerAllocation,
        ...mutation,
    };
};

export const usePreviewAccountEquityOrderMutation = () =>
    useMutation({
        onError,
        mutationFn: tradingMutations.previewAccountEquityOrder,
        mutationKey: ['previewAccountEquityOrder'],
    });

export const usePreviewAlgoEquityOrderMutation = () =>
    useMutation({
        onError,
        mutationFn: tradingMutations.previewAlgoEquityOrder,
        mutationKey: ['previewAlgoEquityOrder'],
    });

export const usePreviewInventoryEquityOrderMutation = () =>
    useMutation({
        onError,
        mutationFn: tradingMutations.previewInventoryEquityOrder,
        mutationKey: ['previewInventoryEquityOrder'],
    });

export const usePreviewModelEquityOrderMutation = () =>
    useMutation({
        onError,
        mutationFn: tradingMutations.previewModelEquityOrder,
        mutationKey: ['previewModelEquityOrder'],
    });

const ordersListingForBranchQueryKey = [...ordersQueries.all().queryKey, 'listingForBranch'];
const ordersListingForModelQueryKey = [...ordersQueries.all().queryKey, 'listingForModel'];
const branchInventoryQueryKey = [...branchQueries.all().queryKey, 'branchInventory'];
const ordersListingQueryKey = [...ordersQueries.all().queryKey, 'listing'];
const portfolioQueryKey = [...userQueries.all().queryKey, 'portfolio'];
const accountHistoryQueryKeyPrefix = [...userQueries.all().queryKey, 'accountHistory'];
const tradingOrderStatusQueryKey = [...tradingQueries.all().queryKey, 'orderStatus'];

const invalidateOrderCache = (queryClient: ReturnType<typeof useQueryClient>, keys: QueryKey[]) => () => {
    keys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
};

export const usePlaceAccountEquityOrderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        onSuccess: invalidateOrderCache(queryClient, [portfolioQueryKey, ordersListingQueryKey]),
        mutationFn: tradingMutations.placeAccountEquityOrder,
        mutationKey: ['placeAccountEquityOrder'],
        onError,
    });
};

export const usePlaceAlgoEquityOrderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        onSuccess: invalidateOrderCache(queryClient, [portfolioQueryKey, ordersListingQueryKey]),
        mutationFn: tradingMutations.placeAlgoEquityOrder,
        mutationKey: ['placeAlgoEquityOrder'],
        onError,
    });
};

export const usePlaceInventoryEquityOrderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        onSuccess: invalidateOrderCache(queryClient, [
            ordersListingForBranchQueryKey,
            branchInventoryQueryKey,
            portfolioQueryKey,
        ]),
        mutationFn: tradingMutations.placeInventoryEquityOrder,
        mutationKey: ['placeInventoryEquityOrder'],
        onError,
    });
};

export const usePlaceModelEquityOrderMutation = () => {
    const queryClient = useQueryClient();
    return useMutation({
        onSuccess: invalidateOrderCache(queryClient, [
            ordersListingForBranchQueryKey,
            ordersListingForModelQueryKey,
            portfolioQueryKey,
        ]),
        mutationFn: tradingMutations.placeModelEquityOrder,
        mutationKey: ['placeModelEquityOrder'],
        onError,
    });
};

type TradingCancelOrderMutationOptions = UseMutationOptions<string, AxiosError, TradingCancelOrderParams>;

export const useTradingCancelOrderMutation = (options?: TradingCancelOrderMutationOptions) => {
    const queryClient = useQueryClient();
    const { onSuccess: userOnSuccess, onError: userOnError, ...restOptions } = options ?? {};

    return useMutation({
        mutationKey: ['tradingCancelOrder'],
        mutationFn: tradingMutations.cancelOrder,
        ...restOptions,
        onSuccess: (_orderRef, variables, context) => {
            toast.success(`Order ${variables.orderRef} has been canceled.`);
            invalidateOrderCache(queryClient, [
                ordersListingForBranchQueryKey,
                ordersListingForModelQueryKey,
                ordersListingQueryKey,
                portfolioQueryKey,
                branchInventoryQueryKey,
                accountHistoryQueryKeyPrefix,
                tradingOrderStatusQueryKey,
            ])();
            userOnSuccess?.(_orderRef, variables, context);
        },
        onError: (error, variables, context) => {
            const details = formatErrorMessage(error.response?.data);
            const ref = variables?.orderRef;
            toast.error(
                ref
                    ? `Failed to cancel order ${ref}${details ? ` (${details})` : ''}`
                    : `Failed to cancel order${details ? `: ${details}` : ''}`,
            );
            userOnError?.(error, variables, context);
        },
    });
};

type TradingModifyOrderMutationOptions = UseMutationOptions<string, AxiosError, TradingModifyOrderParams>;

export const useTradingModifyOrderMutation = (options?: TradingModifyOrderMutationOptions) => {
    const queryClient = useQueryClient();
    const { onSuccess: userOnSuccess, onError: userOnError, ...restOptions } = options ?? {};

    return useMutation({
        mutationKey: ['tradingModifyOrder'],
        mutationFn: tradingMutations.modifyOrder,
        ...restOptions,
        onSuccess: (orderRef, variables, context) => {
            toast.success(`Order ${orderRef} has been modified.`);
            invalidateOrderCache(queryClient, [
                ordersListingForBranchQueryKey,
                ordersListingForModelQueryKey,
                ordersListingQueryKey,
                portfolioQueryKey,
                branchInventoryQueryKey,
                accountHistoryQueryKeyPrefix,
                tradingOrderStatusQueryKey,
            ])();
            userOnSuccess?.(orderRef, variables, context);
        },
        onError: (error, variables, context) => {
            const details = formatErrorMessage(error.response?.data);
            const ref = variables?.values?.orderRef;
            toast.error(
                ref
                    ? `Failed to modify order ${ref}${details ? ` (${details})` : ''}`
                    : `Failed to modify order${details ? `: ${details}` : ''}`,
            );
            userOnError?.(error, variables, context);
        },
    });
};
